import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Calendar, User } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import { useBlog } from '../contexts/BlogContext';
import useSitemapUpdater from '../hooks/useSitemapUpdater';

const BlogPage = () => {
  const { posts, loading } = useBlog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');

  const categoryFromUrl = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoryFromUrl);

  useSitemapUpdater();

  useEffect(() => {
    const category = searchParams.get('category');
    setSelectedCategory(category);
  }, [searchParams]);

  const publishedPosts = posts.filter(post => post.status === 'published');

  const staticCategories = ['Water Problems', 'Tech Brief', 'Cold Chain', 'Field Notes', 'News'];

  const searchPosts = (post: any, term: string) => {
    const searchTerm = term.toLowerCase();
    return (
      (post.title || '').toLowerCase().includes(searchTerm) ||
      (post.excerpt || '').toLowerCase().includes(searchTerm) ||
      (post.content || '').toLowerCase().includes(searchTerm) ||
      (post.author || '').toLowerCase().includes(searchTerm) ||
      (Array.isArray(post.tags) && post.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm)))
    );
  };

  const filteredPosts = publishedPosts
    .filter(post =>
      (searchTerm === '' || searchPosts(post, searchTerm)) &&
      (selectedCategory === null || (Array.isArray(post.tags) && post.tags.includes(selectedCategory)))
    )
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Better Water Blog - CTC Waterworks"
        description="Expert insights on water filtration, sulfur burners, hydrocoolers, and agricultural solutions from the team at CTC Waterworks."
        canonical="https://ctcwaterworks.com/blog"
      />
      <Navbar />

      <section className="pt-24 pb-12 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
              Better Water <span className="text-ctc-blue">Blog</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Expert insights on water solutions, filtration technology, and agricultural innovation from the team at CTC Waterworks.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 max-w-3xl mx-auto bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm"
          >
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Note:</span> A recent site update caused a portion of our older blog content to be temporarily missing. We are restoring those posts and improving formatting as we go. If you have a broken link or need a copy, <Link to="/contact" className="underline hover:text-blue-700 transition-colors">contact us</Link> and we will help immediately.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-6 bg-white border-b border-gray-100 sticky top-16 z-30 bg-white/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center space-x-4 overflow-x-auto scrollbar-hide pb-2">
            <button
              onClick={() => handleCategorySelect(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === null
                  ? 'bg-ctc-blue text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {staticCategories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategorySelect(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  category === selectedCategory
                    ? 'bg-ctc-blue text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-2xl font-bold text-gray-700 mb-4">No articles found</h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || selectedCategory
                  ? `No articles match your search${searchTerm ? ` for "${searchTerm}"` : ''}${selectedCategory ? ` in "${selectedCategory}"` : ''}.`
                  : 'Check back soon for new content!'}
              </p>
              {(searchTerm || selectedCategory) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    handleCategorySelect(null);
                  }}
                  className="px-6 py-3 bg-ctc-blue text-white rounded-lg hover:bg-ctc-blue-dark transition-colors"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="border-b border-gray-100 pb-8 last:border-0"
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block group"
                  >
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                      {post.imageUrl && (
                        <div className="flex-shrink-0 w-full sm:w-48 h-48 sm:h-32 rounded-lg overflow-hidden">
                          <img
                            src={post.imageUrl}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-ctc-blue transition-colors leading-tight">
                          {post.title}
                        </h2>

                        <div className="flex items-center text-sm text-gray-500 mb-3 gap-4">
                          <div className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            <span>{formatDate(post.date)}</span>
                          </div>
                          <div className="flex items-center">
                            <User size={14} className="mr-1" />
                            <span>{post.author}</span>
                          </div>
                        </div>

                        <p className="text-gray-600 leading-relaxed mb-3">
                          {post.excerpt}
                        </p>

                        <div className="flex flex-wrap gap-2">
                          {post.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600"
                            >
                              {tag}
                            </span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              +{post.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default BlogPage;
